import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FEScenario, FEScenarioMachine } from '@/types/flexe';
import { useToast } from '@/hooks/use-toast';

export function useFEScenarios() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: scenarios = [], isLoading } = useQuery({
    queryKey: ['fe-scenarios'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fe_scenarios')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data as FEScenario[];
    }
  });

  const createScenario = useMutation({
    mutationFn: async (scenario: Omit<FEScenario, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('fe_scenarios')
        .insert({ ...scenario, tenant_id: user.id })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fe-scenarios'] });
      toast({ title: 'Scenario toegevoegd' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Fout bij toevoegen scenario', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  const updateScenario = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FEScenario> }) => {
      const { error } = await supabase
        .from('fe_scenarios')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fe-scenarios'] });
      toast({ title: 'Scenario bijgewerkt' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Fout bij bijwerken scenario', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  const deleteScenario = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('fe_scenarios')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fe-scenarios'] });
      toast({ title: 'Scenario verwijderd' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Fout bij verwijderen scenario', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  return {
    scenarios,
    isLoading,
    createScenario: createScenario.mutate,
    updateScenario: updateScenario.mutate,
    deleteScenario: deleteScenario.mutate,
    isCreating: createScenario.isPending,
    isUpdating: updateScenario.isPending,
    isDeleting: deleteScenario.isPending
  };
}

// Hook for managing scenario-machine relationships
export function useFEScenarioMachines(scenarioId?: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: scenarioMachines = [], isLoading } = useQuery({
    queryKey: ['fe-scenario-machines', scenarioId],
    queryFn: async () => {
      if (!scenarioId) return [];
      
      const { data, error } = await supabase
        .from('fe_scenario_machines')
        .select(`
          *,
          machine:fe_machines(*)
        `)
        .eq('scenario_id', scenarioId);
      
      if (error) throw error;
      return data as FEScenarioMachine[];
    },
    enabled: !!scenarioId
  });

  const addMachineToScenario = useMutation({
    mutationFn: async ({ 
      scenarioId, 
      machineId, 
      role 
    }: { 
      scenarioId: string; 
      machineId: string; 
      role: 'new' | 'replacee' 
    }) => {
      const { data, error } = await supabase
        .from('fe_scenario_machines')
        .insert({ scenario_id: scenarioId, machine_id: machineId, role })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fe-scenario-machines'] });
      toast({ title: 'Machine toegevoegd aan scenario' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Fout bij toevoegen machine', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  const removeMachineFromScenario = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('fe_scenario_machines')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fe-scenario-machines'] });
      toast({ title: 'Machine verwijderd uit scenario' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Fout bij verwijderen machine', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  return {
    scenarioMachines,
    isLoading,
    addMachineToScenario: addMachineToScenario.mutate,
    removeMachineFromScenario: removeMachineFromScenario.mutate,
    isAdding: addMachineToScenario.isPending,
    isRemoving: removeMachineFromScenario.isPending
  };
}
